/**
 * „HEUTE DRAN“ (Rafael, 25.10.2026: „Mein Tag — die Reihenfolge wird pro Tag neu angeordnet“) — Phase 1, rein im Client.
 *
 * Aus den vorhandenen Feldern (Arbeitsplan: Prioritaet, Frist, Minuten; Unterziel-Termin; braucht dich) wird ein VORSCHLAG fuer heute gerechnet. Rafael darf ihn fuer HEUTE
 * aendern (nach oben/unten, „Als Nächstes“, „Später“); am naechsten Tag beginnt wieder der Vorschlag. Gespeichert werden nur Buehnen-IDs in seiner Reihenfolge (hoechstens
 * 20), nie Titel oder Inhalte. Rein: kein Fenster, kein Speicher — der Ort des Speicherns ist `useUiPreferences`.
 */
import { aufwandWort, brauchtDich, dringlichkeitsRang, DIVERS_NAME, statusVon, zielDatum, zuordnungsGrund, type OberthemaBuehne } from './guide-oberthemen';

export const HEUTE_MAX = 5;
export const HEUTE_MAX_IDS = 20;
export const HEUTE_MAX_NUTZER = 50;

/** Rafaels Aenderungen fuer EINEN Tag. `reihenfolge`: seine Reihenfolge der sichtbaren Eintraege; `pin`: „Als Nächstes“; `spaeter`: fuer heute ausgeblendet; `zu`: Leiste eingeklappt. */
export interface HeuteWahl { reihenfolge: string[]; pin: string | null; spaeter: string[]; zu: boolean }
export const LEERE_HEUTE_WAHL: HeuteWahl = { reihenfolge: [], pin: null, spaeter: [], zu: false };
export type HeuteTagWahl = HeuteWahl & { tag: string };
export type HeuteMap = Record<string, HeuteTagWahl>;

const TAG = /^\d{4}-\d{2}-\d{2}$/;
const zwei = (n: number) => String(n).padStart(2, '0');
/** Der Tag in der Ortszeit des Fensters, „2026-10-25“ — der Schluessel fuer „nur heute“. */
export function tagesSchluessel(jetzt: number): string {
  const d = new Date(jetzt);
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
}
const kurzDatum = (tag: string) => `${tag.slice(8, 10)}.${tag.slice(5, 7)}.`;

/**
 * Der Tag der Frist (Ortszeit) oder null, wenn es keine lesbare Frist gibt — nichts wird geraten. Gelesen wird, was die Doku verlangt: eine ISO-Zeit mit Zeitzone. Ein deutsches
 * „TT.MM.JJJJ“ zaehlt BEWUSST nicht (Date.parse deutet es nicht eindeutig): die Buehne steht dann mit der Marke „Frist offen“ da, nichts wird still gerechnet.
 */
function fristTag(b: OberthemaBuehne): string | null {
  const f = b.arbeitsplan?.frist;
  if (!f) return null;
  const t = Date.parse(f);
  return Number.isNaN(t) ? null : tagesSchluessel(t);
}

export interface HeuteKandidat<T extends OberthemaBuehne> { b: T; stufe: 0 | 1 | 2 | 3; grund: string; termin: string | null }
/**
 * Der Vorschlag, vollstaendig und geordnet. Stufen: 0 Frist heute oder ueberfaellig · 1 braucht dich · 2 entblockte Arbeit (oder Frist spaeter) · 3 Zielarbeit.
 * Zurueckgestellte (Pausenraum) und Buehnen ohne diese Gruende kommen nicht vor. Gleichstand: frueherer Termin (Frist, sonst Zieltermin), dann Dringlichkeit, dann Titel.
 */
export function heuteKandidaten<T extends OberthemaBuehne>(buehnen: T[], jetzt: number): HeuteKandidat<T>[] {
  const heute = tagesSchluessel(jetzt);
  const aus: HeuteKandidat<T>[] = [];
  for (const b of buehnen) {
    /* Dieselbe Statuslogik wie Zaehler und Filter (statusVon): pausiert fehlt; „wartet auf anderes“ steht nur mit einer Frist heute/ueberfaellig da; „kein naechster Schritt“
       steht nie hier, sondern als Hinweiszeile unter der Leiste (der Berater schuldet den Schritt, Rafael kann nichts tun). Nur eine offene Frage oder Zustimmung ist „braucht dich“. */
    const s = statusVon(b);
    if (s === 'abgeschlossen' || s === 'pausiert' || s === 'ohneSchritt' || s === 'imBau') continue; // im Bau: noch nichts zu oeffnen
    const frist = fristTag(b);
    if (s === 'anderes' && !(frist && frist <= heute)) continue;
    const prio = b.arbeitsplan?.prioritaet;
    let stufe: 0 | 1 | 2 | 3; let grund: string;
    if (frist && frist <= heute) { stufe = 0; grund = frist < heute ? 'überfällig' : 'Frist heute'; }
    else if (brauchtDich(b)) { stufe = 1; grund = 'braucht dich'; }
    else if (prio === 'entblocken' || prio === 'frist') { stufe = 2; grund = prio === 'frist' ? (frist ? `Frist ${kurzDatum(frist)}` : 'Frist offen') : 'entblockt'; }
    else if (prio === 'ziel') { stufe = 3; grund = 'Zielarbeit'; }
    else continue;
    aus.push({ b, stufe, grund, termin: frist ?? zielDatum(b.unterziel_ziel) });
  }
  return aus.sort((a, c) => a.stufe - c.stufe
    || (a.termin ?? '9999-12-31').localeCompare(c.termin ?? '9999-12-31')
    || dringlichkeitsRang(a.b) - dringlichkeitsRang(c.b)
    || a.b.titel.localeCompare(c.b.titel, 'de')
    || a.b.id.localeCompare(c.b.id));
}

export interface HeuteEintrag<T extends OberthemaBuehne> { b: T; grund: string; /** Nur, wenn es eine echte Schaetzung gibt — sonst null (nie erfunden). */ aufwand: string | null; pinned: boolean }
/**
 * Was die Leiste zeigt: der Vorschlag, darauf Rafaels Aenderungen fuer heute. Ein Vorschlag des Assistenten (`vorschlagIds`, Phase 2) ordnet die Kandidaten um; Kennungen,
 * die keine Kandidaten sind, werden ignoriert. Reihenfolge: Rafaels Reihenfolge, dann der Rest im Vorschlag; „Als Nächstes“ steht vorn; „Später“ fehlt.
 */
export function heuteAnordnung<T extends OberthemaBuehne>(buehnen: T[], wahl: HeuteWahl, jetzt: number, opt: { vorschlagIds?: readonly string[]; max?: number } = {}): HeuteEintrag<T>[] {
  let basis = heuteKandidaten(buehnen, jetzt);
  if (opt.vorschlagIds?.length) {
    const nachId = new Map(basis.map(k => [k.b.id, k]));
    const vorn = opt.vorschlagIds.map(id => nachId.get(id)).filter((k): k is HeuteKandidat<T> => !!k);
    const gesetzt = new Set(vorn.map(k => k.b.id));
    basis = [...vorn, ...basis.filter(k => !gesetzt.has(k.b.id))];
  }
  const spaeter = new Set(wahl.spaeter);
  const sichtbar = basis.filter(k => !spaeter.has(k.b.id));
  const nachId = new Map(sichtbar.map(k => [k.b.id, k]));
  const eigene = wahl.reihenfolge.map(id => nachId.get(id)).filter((k): k is HeuteKandidat<T> => !!k);
  const gesetzt = new Set(eigene.map(k => k.b.id));
  let liste = [...eigene, ...sichtbar.filter(k => !gesetzt.has(k.b.id))];
  const pin = wahl.pin && nachId.has(wahl.pin) ? wahl.pin : null;
  if (pin) liste = [nachId.get(pin)!, ...liste.filter(k => k.b.id !== pin)];
  return liste.slice(0, opt.max ?? HEUTE_MAX).map(k => ({ b: k.b, grund: k.grund, aufwand: k.b.arbeitsplan?.minuten ? aufwandWort(k.b) : null, pinned: k.b.id === pin }));
}

/** Verwirft beim Lesen alles, was es nicht mehr gibt (abgeschlossene oder unbekannte Buehnen): nur bekannte IDs bleiben, hoechstens 20 je Liste. */
export function bereinigeHeuteWahl(wahl: HeuteWahl, bekannt: ReadonlySet<string>): HeuteWahl {
  const ids = (l: string[]) => l.filter(id => bekannt.has(id)).slice(0, HEUTE_MAX_IDS);
  return { reihenfolge: ids(wahl.reihenfolge), pin: wahl.pin && bekannt.has(wahl.pin) ? wahl.pin : null, spaeter: ids(wahl.spaeter), zu: wahl.zu };
}

/** Eintrag eine Stelle nach oben (-1) oder unten (+1) in der SICHTBAREN Liste. Beruehrt der Tausch den „Als Nächstes“-Eintrag, entfaellt das Anpinnen (Rafael ordnet bewusst um). */
export function verschiebeEintrag(wahl: HeuteWahl, sichtbareIds: readonly string[], id: string, richtung: -1 | 1): HeuteWahl {
  const i = sichtbareIds.indexOf(id);
  const j = i + richtung;
  if (i < 0 || j < 0 || j >= sichtbareIds.length) return wahl;
  const neu = [...sichtbareIds];
  [neu[i], neu[j]] = [neu[j], neu[i]];
  const beruehrtPin = wahl.pin !== null && (wahl.pin === sichtbareIds[i] || wahl.pin === sichtbareIds[j]);
  return { ...wahl, reihenfolge: neu.slice(0, HEUTE_MAX_IDS), pin: beruehrtPin ? null : wahl.pin };
}
/** „Als Nächstes“: genau einer; derselbe nochmal nimmt es zurueck. */
export const pinneEintrag = (wahl: HeuteWahl, id: string): HeuteWahl => ({ ...wahl, pin: wahl.pin === id ? null : id });
/** „Später“: fuer heute ausgeblendet (die letzten 20), nicht mehr angepinnt oder umgeordnet. */
export const spaeterEintrag = (wahl: HeuteWahl, id: string): HeuteWahl => ({
  ...wahl, spaeter: [...wahl.spaeter.filter(x => x !== id), id].slice(-HEUTE_MAX_IDS), pin: wahl.pin === id ? null : wahl.pin, reihenfolge: wahl.reihenfolge.filter(x => x !== id),
});
/** Zurueck zum Vorschlag: Reihenfolge, Pin und „Später“ weg, die Einklapp-Wahl bleibt. */
export const zumVorschlag = (wahl: HeuteWahl): HeuteWahl => ({ ...LEERE_HEUTE_WAHL, zu: wahl.zu });
export const setzeZu = (wahl: HeuteWahl, zu: boolean): HeuteWahl => ({ ...wahl, zu });
export const istGeaendert = (wahl: HeuteWahl): boolean => wahl.reihenfolge.length > 0 || wahl.pin !== null || wahl.spaeter.length > 0;

// ── Speicherform: je Nutzer die Wahl des heutigen Tages ──────────────────────────────────────────────────────────────────────────
/** Liest den gespeicherten Wert streng; alles Unpassende wird verworfen (kaputter Speicher: leer, kein Absturz). */
export function bereinigeHeuteMap(roh: unknown): HeuteMap {
  const aus: HeuteMap = {};
  if (typeof roh !== 'object' || roh === null || Array.isArray(roh)) return aus;
  const ids = (x: unknown): string[] => Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string' && s.length > 0 && s.length <= 200).slice(0, HEUTE_MAX_IDS) : [];
  for (const [nutzer, w] of Object.entries(roh as Record<string, unknown>).slice(0, HEUTE_MAX_NUTZER)) {
    if (!nutzer || nutzer.length > 100 || typeof w !== 'object' || w === null) continue;
    const o = w as Record<string, unknown>;
    if (typeof o.tag !== 'string' || !TAG.test(o.tag)) continue;
    aus[nutzer] = { tag: o.tag, reihenfolge: ids(o.reihenfolge), pin: typeof o.pin === 'string' && o.pin.length <= 200 && o.pin ? o.pin : null, spaeter: ids(o.spaeter), zu: o.zu === true };
  }
  return aus;
}
/** Die Wahl dieses Nutzers fuer DIESEN Tag; von einem anderen Tag gilt nichts (am naechsten Tag beginnt wieder der Vorschlag) — nur das Einklappen darf bleiben. */
export function heuteWahlFuer(karte: HeuteMap, nutzer: string, tag: string): HeuteWahl {
  const w = karte[nutzer];
  if (!w) return LEERE_HEUTE_WAHL;
  if (w.tag !== tag) return { ...LEERE_HEUTE_WAHL, zu: w.zu };
  return { reihenfolge: w.reihenfolge, pin: w.pin, spaeter: w.spaeter, zu: w.zu };
}
export function merkeHeuteWahl(karte: HeuteMap, nutzer: string, tag: string, wahl: HeuteWahl): HeuteMap {
  if (!nutzer) throw new Error('merkeHeuteWahl: Nutzerkennung fehlt');
  if (!TAG.test(tag)) throw new Error(`merkeHeuteWahl: ungueltiger Tag „${tag}“`);
  const ohne = Object.entries(karte).filter(([k]) => k !== nutzer);
  return Object.fromEntries([...ohne.slice(Math.max(0, ohne.length - (HEUTE_MAX_NUTZER - 1))), [nutzer, { ...wahl, tag }]]);
}

// ── Warteschlange der geöffneten Bühne (Phase 1 der Guide-Fläche) ───────────────────────────────────────────────────────────────────
/**
 * Die Reihenfolge fuer „Weiter“ und „Zurück“: dieselbe wie „Heute dran“ (Rafaels Tagesreihenfolge, sonst der Vorschlag; ein Vorschlag des Assistenten ordnet um), aber ohne die
 * Grenze von fuenf — unabhaengig davon, ob er eine Buehne schon „gesehen“ hat. Die offene Buehne kann herausfallen (z. B. nach einer Antwort); dann zeigt `letzterIndex` ihren
 * bisherigen Platz, und „Weiter“ ist der, der jetzt dort steht.
 */
export interface Warteschlange<T extends OberthemaBuehne> {
  liste: T[];
  /** Platz der offenen Buehne in der Liste, `-1` wenn sie nicht (mehr) dazugehoert. */
  index: number;
  zurueck: T | null;
  weiter: T | null;
  quelle: 'heute' | 'vorschlag' | 'assistent';
}
export function warteschlange<T extends OberthemaBuehne>(buehnen: T[], wahl: HeuteWahl, jetzt: number, offeneId: string | null, opt: { vorschlagIds?: readonly string[]; letzterIndex?: number | null } = {}): Warteschlange<T> {
  const lebend = buehnen.filter(b => !(b as { abschluss?: unknown }).abschluss);
  const liste = heuteAnordnung(lebend, wahl, jetzt, { vorschlagIds: opt.vorschlagIds, max: 1000 }).map(e => e.b);
  const index = offeneId ? liste.findIndex(b => b.id === offeneId) : -1;
  const quelle = istGeaendert(wahl) ? 'heute' : opt.vorschlagIds?.length ? 'assistent' : 'vorschlag';
  if (index >= 0) return { liste, index, zurueck: liste[index - 1] ?? null, weiter: liste[index + 1] ?? null, quelle };
  const pos = opt.letzterIndex != null && opt.letzterIndex >= 0 ? Math.min(opt.letzterIndex, liste.length) : 0;
  return { liste, index: -1, zurueck: opt.letzterIndex != null && opt.letzterIndex > 0 ? (liste[pos - 1] ?? null) : null, weiter: liste[pos] ?? null, quelle };
}

const PRIO_KURZ: Record<string, string> = { frist: 'Frist', entblocken: 'entblockt', ziel: 'Zielarbeit', spaeter: 'später' };
export interface ZielZeile { pfad: string; divers: boolean; grund: string | null; meta: string[] }
/**
 * „Oberthema › Unterziel · Frist/Priorität“ ueber der geoeffneten Buehne. Ohne Board-Oberthema mit lesbarem Unterziel steht „Divers · noch einzuordnen“ — mit dem Grund
 * (`zuordnungsGrund`), nichts wird verschleiert. Meta: Frist (nur lesbar), Zieltermin des Unterziels (Text wie im Board), Prioritaet.
 */
export function zielZeile(b: OberthemaBuehne): ZielZeile {
  const grund = zuordnungsGrund(b);
  const meta: string[] = [];
  const frist = fristTag(b);
  if (frist) meta.push(`Frist ${kurzDatum(frist)}`);
  if (b.unterziel_ziel && !grund) meta.push(`Ziel ${b.unterziel_ziel}`);
  const prio = b.arbeitsplan?.prioritaet;
  if (prio && PRIO_KURZ[prio] && !(prio === 'frist' && frist)) meta.push(PRIO_KURZ[prio]);
  return grund
    ? { pfad: DIVERS_NAME, divers: true, grund, meta }
    : { pfad: `${b.thema_name} › ${b.unterziel_name}`, divers: false, grund: null, meta };
}
